/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 * 
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 * 
 * Software credits: 
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 * 
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

/**
 * ### Analysis Report
 * ![header](../images/frontend/manage-the-bot/analysis-report.png)
 *  @function
 * @memberOf ManageTheBot
 * @name Corpus-AnalysisReport
 */

import React, { useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppContext } from 'commons/contexts/AppContext';

import agentActions from '@store/actions/agents';
import { isEmpty } from '@utils/validation/isEmpty';
import { useAgentId } from '@hooks/useAgentId';
import { MESSAGES } from '@translations/labels';
import DSPage from '@design-system/page/DSPage';
import { PRIORITIES } from '@constants';
import { LastTrainingTable } from './table/lastTrainingTable';
import { ManageBotHeader } from '../manageBotHeader';
import { CorpusHeader } from '../corpus/corpusHeader';
import { Spin } from 'antd';

const mapModelAnalyzerReport = report => {
  if (!report || !report.issues) {
    return [];
  }
  const issues = report.issues.map((issue, idx) => {
    const observations0 = issue.observations && issue.observations[0];
    const utterance0 = issue.observations && issue.observations[0].utterance;
    const intent1 = issue.observations[1] && issue.observations[1].intent;
    const intentId1 = issue.observations[1] && issue.observations[1].intentId;
    const priority1 = issue.observations[1] && issue.observations[1].priority;
    const utterance1 = issue.observations[1] && issue.observations[1].utterance;
    return {
      id: idx + 1,
      type: issue.level,
      source: issue.source,
      reason: issue.reason,
      ...(issue.observations &&
        observations0 && {
          intent0: observations0.intent,
          intentId0: observations0.intentId,
          priority: PRIORITIES.find(priority => priority.value === observations0.priority).name.toUpperCase(),
          utterances: observations0.utterances,
          ...(observations0.definedUtterances && {
            definedUtterances: observations0.definedUtterances,
          }),
        }),
      ...(utterance0 && { utterance0 }),
      ...(issue.quality &&
        issue.quality.secondClassification &&
        issue.quality.secondClassification.intent && {
          secondClassification: issue.quality.secondClassification.intent,
        }),
      ...(issue.collisions && { collisions: issue.collisions }),
      ...(issue.collisionIntent && { collisionIntent: issue.collisionIntent }),
      ...(issue.observations &&
        issue.observations.length > 1 && {
          intent1,
          intentId1,
          priority1,
          utterance1,
        }),
    };
  });

  const { id, createdAt } = report;

  return {
    issues,
    id,
    createdAt,
  };
};

const labels = { ...MESSAGES.commons, ...MESSAGES.manageBot };

const LastTrainingPage = () => {
  const dispatch = useDispatch();
  const { agentId } = useAgentId();
  const selectedVersion = useSelector(state => state.corpusReleases.currentVersionId);
  const analysisStatus = useSelector(state => state.agents?.updates?.analyzer?.status);
  const { isAnalysePending } = useContext(AppContext);

  useEffect(() => {
    if (!isEmpty(agentId) && analysisStatus !== 'Analyzing' && !isAnalysePending)
      dispatch(agentActions.getLastModelAnalyzerReport(agentId, selectedVersion?.versionId));
  }, [agentId, selectedVersion, analysisStatus === 'Analyzing', isAnalysePending]);

  const modelAnalyzerReport = useSelector(state => state.agents.modelAnalyzerReport);
  const { issues, id, createdAt } = mapModelAnalyzerReport(modelAnalyzerReport);
  const options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' };
  const date = createdAt ? new Date(createdAt).toLocaleString('en-UK', options) : null;
  return (
    <div className="manage-bot-content last-training overflow-y-auto">
      <ManageBotHeader />
      <CorpusHeader />
      {analysisStatus !== 'Analyzing' && !isAnalysePending && (
        <DSPage>
          {date && (
            <>
              <header className="flex space-between">
                <h4 className="uppercase">
                  {labels.lastTraining}: {date}
                </h4>
              </header>
              <LastTrainingTable issues={issues} id={id} />
            </>
          )}
          {!date && (
            <header style={{ display: 'flex', flexDirection: 'column', marginLeft: '10px' }}>
              <h5 className="uppercase">No analysis report has been created for this corpus version.</h5>
              <br />
              <p>
                You can proceed now by selecting <b>Analyse</b> button
              </p>
            </header>
          )}
        </DSPage>
      )}
      {(analysisStatus === 'Analyzing' || isAnalysePending) && (
        <div style={{ width: '100%', marginLeft: '50%', marginTop: '150px' }}>
          <Spin size="large" />
        </div>
      )}
    </div>
  );
};

export { LastTrainingPage };
